import { styled } from "styled-components";

import { QuillPenIcon } from "@vericus/cadmus-icons";
import { Button, Colored, desktop, Divider, Text } from "@vericus/cadmus-ui";

import finalImg from "@/assets/illustrations/student-running-and-checkmark.png";
import { FocusTitle } from "@/components/focus-title";
import { type AssessmentNoun, responseTimeTerm } from "@/features/assignment";
import { useCountdown } from "@/features/timeline";
import { useRespondusLDB } from "@/hooks/use-respondus-ldb";
import { formatDateFull } from "@/utils/datetime";

import { ActionContainer, Illustration } from "./styles";

interface ExamProps {
  assessmentName: string;
  assessmentNoun: AssessmentNoun;
  userName: string;
  submittedAt: string;
  userEmail?: string;
  /** Valid future writing date, if there is still exam time left. */
  writingDate?: string;
  /** Optional callback to continue editing if available */
  onContinueEditing?: () => void;
  /** Whether the assessment has a viva enabled, reframing writing as response. */
  vivaEnabled: boolean;
}

/**
 * Confirmation message for a successful exam final submission.
 */
export function ExamSubmissionConfirmation(props: ExamProps) {
  const { writingDate, onContinueEditing, vivaEnabled } = props;
  const { isLDBExam, exitLDB, isIpad } = useRespondusLDB();

  const title =
    props.assessmentNoun === "exam"
      ? "Your exam has been submitted"
      : "Your response has been submitted";

  return (
    <Container>
      <FocusTitle kind="displayTwo">{title}</FocusTitle>
      <GridDivider />
      <InfoContainer>
        <Heading>
          <Text kind="headingFour" as="h2">
            {props.assessmentName}
          </Text>
          <Text kind="label">Submitted by {props.userName}</Text>
        </Heading>

        <div>
          <Text kind="bodyMd">
            On {formatDateFull(new Date(props.submittedAt))}
          </Text>
          {props.userEmail != null && (
            <Text kind="bodyMd">
              A confirmation of your submission has been sent to:{" "}
              <Colored color="blue500">{props.userEmail}</Colored>
            </Text>
          )}
        </div>
        {/*
         * Show actions when the student can continue editing or is in an LDB exam
         * to help students exit. We deliberately do not gate this on `writingDate` —
         * it can be null when a teacher hard-resets the student's start time
         * for a resit in Window Exam, then the student should still be able to continue
         */}
        {(isLDBExam || onContinueEditing) && (
          <ActionContainer>
            {onContinueEditing && writingDate != null && (
              <TimeRemainingMessage
                endDate={writingDate}
                vivaEnabled={vivaEnabled}
              />
            )}
            <ButtonContainer>
              {onContinueEditing && (
                <Button
                  onClick={onContinueEditing}
                  kind="secondary"
                  icon={<QuillPenIcon size={24} label="" />}
                >
                  Continue Working
                </Button>
              )}
              {isLDBExam && isIpad && (
                <Button onClick={exitLDB}>Close Browser</Button>
              )}
            </ButtonContainer>
          </ActionContainer>
        )}
      </InfoContainer>
      <GridIllustration src={finalImg} alt="" />
    </Container>
  );
}

// Stateful countdown towards the time remaining to continue editing
function TimeRemainingMessage(props: {
  endDate: string;
  vivaEnabled: boolean;
}) {
  const { message } = useCountdown(props.endDate);
  return (
    <Text kind="systemMd" role="timer" aria-live="polite" aria-atomic="true">
      You have {message} of {responseTimeTerm(props.vivaEnabled)} remaining
    </Text>
  );
}

const ButtonContainer = styled.div`
  display: inline-flex;
  gap: 20px;
`;

const Container = styled.div`
  display: grid;
  gap: 16px;
  grid-template:
    "heading" auto
    "divider" auto
    "info" 1fr
    / 1fr;

  ${desktop`
    grid-template:
    "heading illustration" auto
    "divider illustration" auto
    "info illustration" 1fr
    / 1fr auto;
  `}
`;

const Heading = styled.div`
  grid-area: heading;
`;

const GridDivider = styled(Divider)`
  grid-area: divider;
`;

const InfoContainer = styled.div`
  grid-area: info;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GridIllustration = styled(Illustration)`
  height: auto;
  width: 100%;

  grid-area: none;
  display: none;

  ${desktop`
    grid-area: illustration;
    display: block;
  `}
`;
